import React from 'react';
import { cn } from '../../../lib/utils';
import { OrkaSurface } from '../OrkaSurface';
import { OrkaText } from '../OrkaText';
import { getEffortBucket } from '../../../design-system/effortScale';

/**
 * TodayCommandBand — top-of-dashboard metric strip (PRD §8.6).
 *
 * "Today is a planning and execution surface, not a generic dashboard."
 * Dense read of Effort · Focus · Open · Risk with headline date + eyebrow.
 *
 *   <TodayCommandBand
 *     date="Friday, May 1"
 *     eyebrow="Daily command surface"
 *     metrics={{ effort: 34, focusMinutes: 120, open: 12, risk: 3 }}
 *     rightSlot={<Button>End Day</Button>}
 *   />
 */
export function TodayCommandBand({
  date,
  eyebrow = 'Daily command surface',
  title = 'Today',
  subtitle,
  metrics = {},
  metricItems,
  leftSlot,
  rightSlot,
  className,
  ...rest
}) {
  const bucket = getEffortBucket(metrics.effort || 0);
  const items = metricItems || [
    { label: 'Effort', value: metrics.effort ?? '—', sub: bucket.label !== 'Empty' ? bucket.label : undefined, color: metrics.effort ? bucket.color : undefined, testId: 'metric-effort' },
    { label: 'Focus',  value: fmtMinutes(metrics.focusMinutes), testId: 'metric-focus' },
    { label: 'Open',   value: metrics.open ?? '—', testId: 'metric-open' },
    { label: 'Risk',   value: metrics.risk ?? '—', color: metrics.risk > 0 ? 'var(--orka-text-danger)' : undefined, testId: 'metric-risk' },
  ];
  return (
    <OrkaSurface
      variant="raised"
      radius="xl"
      className={cn('p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-6', className)}
      data-testid="today-command-band"
      {...rest}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.18em] uppercase orka-text-intent font-bold">
          {eyebrow}{date ? ` · ${date}` : ''}
        </div>
        <OrkaText as="h2" size="h2" variant="primary" className="mt-1.5 !text-2xl md:!text-3xl !leading-tight">
          {title}
        </OrkaText>
        {subtitle && <OrkaText as="p" size="small" variant="secondary" className="mt-1">{subtitle}</OrkaText>}
        {leftSlot}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="today-command-band-metrics">
        {items.map(it => (
          <Metric
            key={it.testId || it.label}
            label={it.label}
            value={it.value}
            sub={it.sub}
            color={it.color}
            boxColor={it.boxColor}
            testId={it.testId}
          />
        ))}
      </div>
      {rightSlot && <div className="flex items-center gap-2 shrink-0">{rightSlot}</div>}
    </OrkaSurface>
  );
}

function Metric({ label, value, sub, color, boxColor, testId }) {
  return (
    <div
      className="min-w-[86px] rounded-xl border px-3 py-2 orka-transition-colors"
      style={{
        borderColor: boxColor || 'var(--orka-border-subtle, rgba(120,120,120,.22))',
        background: boxColor ? `color-mix(in srgb, ${boxColor} 18%, var(--orka-surface-base, #fff))` : 'var(--orka-surface-base, transparent)',
        boxShadow: boxColor ? `inset 0 0 0 1px ${boxColor}66` : undefined,
      }}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wider orka-text-tertiary font-bold">{label}</div>
      <div
        className="text-xl font-bold tabular-nums mt-0.5"
        style={{ color: color || 'var(--orka-text-primary)' }}
      >
        {value}
      </div>
      {sub && <div className="text-[10px] orka-text-tertiary font-semibold mt-0.5">{sub}</div>}
    </div>
  );
}

function fmtMinutes(m) {
  if (m == null) return '—';
  if (m === 0) return '0';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h${mm}` : `${h}h`;
}

export default TodayCommandBand;
